import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { SidebarComponent } from './sidebar.component';
import { TopbarComponent } from './topbar.component';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SidebarComponent, TopbarComponent],
  template: `
    <div class="admin-shell" [class.sidebar-collapsed]="sidebarCollapsed()">
      <app-sidebar
        [collapsed]="sidebarCollapsed()"
        [mobileOpen]="mobileMenuOpen()"
        (collapseToggle)="toggleNavigation()"
        (navigate)="closeMobileMenu()" />

      <div class="workspace">
        <app-topbar [pageTitle]="pageTitle()" (navigationToggle)="toggleNavigation()" />
        <main class="workspace-body"><router-outlet /></main>
      </div>

      <button
        *ngIf="mobileMenuOpen()"
        class="mobile-backdrop"
        type="button"
        aria-label="Close navigation"
        (click)="closeMobileMenu()"></button>
    </div>
  `,
  styles: [`
    :host{display:block;min-height:100vh}
    .admin-shell{min-height:100vh;display:grid;grid-template-columns:280px minmax(0,1fr);gap:20px;padding:20px;background:#f4f7fb}
    .admin-shell.sidebar-collapsed{grid-template-columns:88px minmax(0,1fr)}
    .workspace{min-width:0;display:grid;align-content:start;gap:18px}
    .workspace-body{min-width:0;padding-bottom:24px}
    .mobile-backdrop{display:none}
    @media(max-width:1100px){
      .admin-shell,.admin-shell.sidebar-collapsed{grid-template-columns:1fr;padding:14px}
      .mobile-backdrop{display:block;position:fixed;inset:0;border:0;background:#15223880;z-index:30}
    }
  `]
})
export class AdminLayoutComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly sidebarCollapsed = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly pageTitle = signal('Dashboard');

  constructor() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.updatePageTitle());
  }

  toggleNavigation(): void {
    const isMobile = window.matchMedia('(max-width: 1100px)').matches;
    if (isMobile) {
      this.sidebarCollapsed.set(false);
      const nextOpenState = !this.mobileMenuOpen();
      this.mobileMenuOpen.set(nextOpenState);
      return;
    }

    this.sidebarCollapsed.update((value) => !value);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  private updatePageTitle(): void {
    let routeSnapshot = this.router.routerState.snapshot.root;
    while (routeSnapshot.firstChild) {
      routeSnapshot = routeSnapshot.firstChild;
    }

    const routeTitle = routeSnapshot.data?.['title'];
    const pageTitle = typeof routeTitle === 'string' && routeTitle.trim()
      ? routeTitle
      : 'Dashboard';

    this.pageTitle.set(pageTitle);
  }
}
